// src/components/AdminDashboard.js (Final Commission Fix with Robust OTA Detection)
import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import {
    collection,
    addDoc,
    getDocs,
    deleteDoc,
    doc,
    query,
    where,
    setDoc
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
// *** NEW IMPORT for Routing ***
import { useNavigate } from 'react-router-dom';
// Helper component for Tab Button (Styling for clean look)
const TabButton = ({ name, active, onClick }) => (
    <button
        onClick={() => onClick(name.toLowerCase().replace(/\s/g, ''))}
        style={{
            padding: '10px 20px',
            marginRight: '10px',
            border: 'none',
            borderRadius: '5px 5px 0 0',
   
         cursor: 'pointer',
            // Assuming a CSS variable or using a placeholder color
            backgroundColor: active ? '#007bff' : '#f0f0f0',
            color: active ? 'white' : '#333',
            fontWeight: active ? 'bold' : 'normal',
            transition: 'background-color 0.2s'
    
     }}
    >
        {name}
    </button>
);
// Initial room/user states
const initialRoomState = {
    roomNumber: '',
    price: '',
    type: 'Standard',
    hotelId: '',
    status: 'available'
};
const initialUserState = {
    email: '',
    password: '',
    role: 'hotel',
    hotelId: '',
    hotelName: ''
};
// 💰 REVENUE CONSTANTS
const DEFAULT_DIRECT_RATE = 0.08; 
const DEFAULT_THIRD_PARTY_RATE = 0.02;
// --- UPDATED COMMISSION CONSTANTS PER USER REQUEST (10% and 25%) ---
const BASE_UPSELL_COMMISSION_RATE = 0.10;
// 10% for general upselling
const PAGE_COMMISSION_RATE = 0.25; // 25% for personalized page commission

// --- ROBUST OTA IDENTIFIERS FOR FLEXIBLE COMMISSION CHECKING (CORE FIX) ---
const OTA_IDENTIFIERS = [
    'OTA', 'BOOKING.COM', 'EXPEDIA', 'AGODA', 'HOTELS.COM', 'AIRBNB', 'THIRD PARTY', '3RD PARTY'
];
// --- END CORE FIX ---

export default function AdminDashboard() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate(); 
    const [hotels, setHotels] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [staff, setStaff] = useState([]);
// <-- NEW: State for staff list
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    // UPDATED: Added 'contacts' to active sections
    const [activeSection, setActiveSection] = useState('hotels');
// UPDATED: Added fields for location and Upsell commission rate
    const [newHotel, setNewHotel] = useState({
        name: '',
        country: '',        // NEW
        state: '',          // NEW
        city: '',           // NEW
        address: '',      
        // NEW (Optional)
        thirdPartyCommissionRate: DEFAULT_THIRD_PARTY_RATE, // OTA Rate
        directCommissionRate: DEFAULT_DIRECT_RATE, // Direct/Staff Rate
        upsellCommissionRate: BASE_UPSELL_COMMISSION_RATE // NEW: Upsell Commission Rate
    });
    const [newRoom, setNewRoom] = useState(initialRoomState);
    const [newUser, setNewUser] = useState(initialUserState);

    const [selectedHotelId, setSelectedHotelId] = useState('');
    const [dashboardCodes, setDashboardCodes] = useState({
        reception: '',
        housekeeping: '',
        inventory: '',
        manager: ''
    });
// --- NEW REVENUE STATE ---
    const [guestRecords, setGuestRecords] = useState([]);
    const [revenueData, setRevenueData] = useState({
        totalRevenue: 0,
        yourCommission: 0,
        upsellRevenue: 0,
        otaCount: 0,
        directCount: 0,
        avgGuestValue: 0,
        occupiedNights: 0,
    });
// --- END NEW REVENUE STATE ---

    // --- NEW CONTACTS STATE ---
    const [newContact, setNewContact] = useState({
        hotelName: '',
        city: '',
        country: '',
        contactInfo: '',
        notes: ''
    });
    const [hotelContacts, setHotelContacts] = useState([]); // To display list
    // --- END NEW CONTACTS STATE ---


    const getHotelName = (id) => hotels.find(h => h.id === id)?.name ||
    'Unknown';

    // ============================================================
    // LOAD ALL HOTELS + ROOMS + GUEST DATA
    // ============================================================
    useEffect(() => {
        if (authLoading) return;

        if (!user || user.role !== 'admin') {
            setLoading(false);
            setError("Access denied. Admin only.");
            return;
        }

    
        const fetchAllData = async () => {
            try {
                const hotelSnap = await getDocs(collection(db, 'hotels'));
                // Ensure commission rates are parsed as float
                const fetchedHotels = hotelSnap.docs.map((d) => ({
        
                id: d.id,
                    ...d.data(),
                    // Use DEFAULT_THIRD_PARTY_RATE if not found/invalid
                    thirdPartyCommissionRate: parseFloat(d.data().thirdPartyCommissionRate) || DEFAULT_THIRD_PARTY_RATE, 
          
                    // NEW: 
 
                    // Ensure direct rate is available
                    directCommissionRate: parseFloat(d.data().directCommissionRate) ||
                    DEFAULT_DIRECT_RATE,
                    // NEW: Ensure upsell rate is available, default to BASE_UPSELL_COMMISSION_RATE (0.10)
                    upsellCommissionRate: parseFloat(d.data().upsellCommissionRate) ||
                    BASE_UPSELL_COMMISSION_RATE 
                }));
                setHotels(fetchedHotels);
                const roomSnap = await getDocs(collection(db, 'rooms'));
                const fetchedRooms = roomSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setRooms(fetchedRooms);
// --- CHANGED: NEW GUEST DATA FETCHING (Source of truth for revenue) ---
                const guestSnap = await getDocs(collection(db, 'guests'));
                const fetchedGuests = guestSnap.docs.map((d) => {
                    const data = d.data();
                    
                    // We must convert Firebase Timestamps to JS Date objects for easy comparison
                    
 
                    const checkInDateValue = data.checkInDate && typeof data.checkInDate.toDate === 'function' ? data.checkInDate.toDate() : new Date(data.checkInDate);
                    const checkOutDateValue = data.checkOutDate && typeof data.checkOutDate.toDate === 'function' ? data.checkOutDate.toDate() : new Date(data.checkOutDate);

                    const checkIn = checkInDateValue;
          
                    const checkOut = checkOutDateValue;


        
                    let nights = 0;
                    if (checkIn && checkOut && checkIn.getTime() < checkOut.getTime()) {
                 
                        nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) 
/ (1000 * 60 * 60 * 24));
                    }

 
                    return {
                        id: d.id,
              
                        ...data,
  
                        checkIn: checkIn,
      
                        checkOut: checkOut,
                        nights: nights
       
                     };
                });
                setGuestRecords(fetchedGuests);
// --- END NEW GUEST DATA FETCHING ---
                
                // --- NEW STAFF DATA FETCHING (ADDED) ---
                // Fetch all users that are not 'admin' (i.e., hotel staff)
                const staffQuery = query(collection(db, 'users'), where('role', '!=', 'admin'));
                const staffSnap = await getDocs(staffQuery);
                const fetchedStaff = staffSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setStaff(fetchedStaff);
// --- END NEW STAFF DATA FETCHING ---

                // --- NEW CONTACTS DATA FETCHING ---
                const contactSnap = await getDocs(collection(db, 'hotel_contacts'));
                const fetchedContacts = contactSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
                setHotelContacts(fetchedContacts);
// --- END NEW CONTACTS DATA FETCHING ---


                if (fetchedHotels.length > 0) {
                    setNewRoom((p) => ({ ...p, hotelId: p.hotelId || fetchedHotels[0].id }));
                    setNewUser((p) => ({
                        ...p,
                        hotelId: p.hotelId || fetchedHotels[0].id,
                        hotelName: p.hotelName || fetchedHotels[0].name
                    
 
                    }));
                }
            } catch (err) {
                console.error(err);
                setError("Failed loading data.");
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [user, authLoading]);
// ============================================================
    // REFRESH DATA
    // ============================================================
    const refreshData = async () => {
        const hotelSnap = await getDocs(collection(db, 'hotels'));
        const fetchedHotels = hotelSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            thirdPartyCommissionRate: parseFloat(d.data().thirdPartyCommissionRate) || DEFAULT_THIRD_PARTY_RATE,
            directCommissionRate: parseFloat(d.data().directCommissionRate) || DEFAULT_DIRECT_RATE,
            // NEW: Ensure upsell rate is available, default to BASE_UPSELL_COMMISSION_RATE (0.10)
            upsellCommissionRate: parseFloat(d.data().upsellCommissionRate) || BASE_UPSELL_COMMISSION_RATE
   
         }));
        setHotels(fetchedHotels);

        const roomSnap = await getDocs(collection(db, 'rooms'));
        setRooms(roomSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
// --- CHANGED: GUEST DATA FETCHING FOR REFRESH ---
        const guestSnap = await getDocs(collection(db, 'guests'));
        const fetchedGuests = guestSnap.docs.map((d) => {
            const data = d.data();
            
            const checkInDateValue = data.checkInDate && typeof data.checkInDate.toDate === 'function' ? data.checkInDate.toDate() : new Date(data.checkInDate);
            const checkOutDateValue = data.checkOutDate && typeof data.checkOutDate.toDate === 'function' ? data.checkOutDate.toDate() : new Date(data.checkOutDate);

            const checkIn = checkInDateValue;
   
            const checkOut = checkOutDateValue;
            
            let nights = 0;
            if (checkIn && checkOut && checkIn.getTime() < checkOut.getTime()) {
                nights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
          
            }
 
            return {
                id: d.id,
                ...data,
                checkIn: checkIn,
                checkOut: checkOut,
       
                 nights: 
                nights
    
            };
        });
        setGuestRecords(fetchedGuests);
        // --- END GUEST DATA FETCHING FOR REFRESH ---
        
        // --- NEW STAFF DATA FETCHING FOR REFRESH (ADDED) ---
        const staffQuery = query(collection(db, 'users'), where('role', '!=', 'admin'));
        const staffSnap = await getDocs(staffQuery);
        setStaff(staffSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
// --- END NEW STAFF DATA FETCHING FOR REFRESH ---
        
        // --- NEW CONTACTS DATA FETCHING FOR REFRESH ---
        const contactSnap = await getDocs(collection(db, 'hotel_contacts'));
        setHotelContacts(contactSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        // --- END NEW CONTACTS DATA FETCHING FOR REFRESH ---
    };
// ============================================================
    // REVENUE CALCULATION EFFECT (Uses guestRecords) - COMMISSION LOGIC FIXED
    // ============================================================
    useEffect(() => {
        if (!guestRecords.length) {
            setRevenueData({
                totalRevenue: 0, yourCommission: 0, upsellRevenue: 0, otaCount: 0,
                directCount: 0, avgGuestValue: 0, occupiedNights: 0,
         
 
            });
            return;
        }

        const now = new Date();
        
        let totalRevenue = 0;
        let totalCommission = 0;
        let totalUpsellRevenue = 0;
        let otaCount = 0;
   
        let directCount = 0;
  
        let totalGuestValue = 0;
        let occupiedNights = 0;
        let bookingCount = 0;

        guestRecords.forEach(guest => {
            // 🚨 Use fields specified by the user for revenue calculation
            const rawUpsells = parseFloat(guest.addOnsPrice) ||
            0; 
            const totalFinalPrice = parseFloat(guest.adjustedFinalPrice) || 0; 

            const taxCollected = parseFloat(guest.taxCollected) || 0; 
            const depositCollected = parseFloat(guest.depositCollected) || 0;
// 1. Calculate the Base Price (Room Rate) for Commission:
            // Base Price = Total Price - Upsells - Tax - Deposit
            const basePriceForCommission = totalFinalPrice - rawUpsells - taxCollected - depositCollected;
// Ensure commission is not calculated on negative values
            const basePrice = Math.max(0, basePriceForCommission);
            const upsells = Math.max(0, rawUpsells); 
            
            const nights = guest.nights || 0;
            
            // --- CORE FIX: NORMALIZE SOURCE AND CHECK AGAINST OTA_IDENTIFIERS ---
            const rawSource = guest.bookingSource || '';
            const source = rawSource.trim().toUpperCase(); 
            
            // *** NEW LOGIC PER USER REQUEST: Direct booking if agreedToTerms is true ***
            // This is the primary override for commission calculation
            const isDirectBookingByTerms = guest.agreedToTerms === true; 
            
            // Check if source text is an explicit OTA (existing logic)
            const isSourceOta = OTA_IDENTIFIERS.some(identifier => source.includes(identifier.replace('.', '')));
            
            // Determine if a source was even provided
            const isSourcePresent = source.length > 0;
            
            // The final flag for commission logic ('isOtaOrUnknown'):
            // Booking is OTA (uses thirdPartyCommissionRate) if it is NOT an explicit Direct Booking by terms
            // AND it either is an OTA source (isSourceOta) or is an unknown source (!isSourcePresent).
            const isOtaOrUnknown = !isDirectBookingByTerms && (isSourceOta || !isSourcePresent);

            // Redefine isOtaBooking for clarity/legacy reference in the table display logic.
            const isOtaBooking = isSourceOta;
            // Robust check: uses includes
            // --- END CORE FIX ---

            // Get the specific hotel's commission rate
            const hotel = hotels.find(h => h.id === guest.hotelId);
// Get the specific rates for the hotel, falling back to defaults if hotel is missing
            let thirdPartyRate = parseFloat(hotel?.thirdPartyCommissionRate) ||
            DEFAULT_THIRD_PARTY_RATE;
            let directRate = parseFloat(hotel?.directCommissionRate) || DEFAULT_DIRECT_RATE;
            // NEW: Get the hotel-specific upsell rate
            let upsellRate = parseFloat(hotel?.upsellCommissionRate) ||
            BASE_UPSELL_COMMISSION_RATE;
            
            // --- REVENUE SPLIT ASSUMPTION ---
            const personalizedPageRevenue = parseFloat(guest.personalizedPageRevenue) ||
            0;
            // General Upsell Revenue is Total Upsells minus the Page Revenue portion
            const baseUpsellRevenue = Math.max(0, upsells - personalizedPageRevenue);
// 1. Calculate Total Guest Value (Final Price) - Use the user's total field (for AVG GUEST VALUE - ALL TIME)
            const finalPrice = totalFinalPrice;
            totalGuestValue += finalPrice;
            bookingCount++;

            // 2. Filter for Current Month for KPIs
            // Ensure checkIn is a valid Date object
            const checkInDate = guest.checkIn instanceof Date && !isNaN(guest.checkIn) ?
            guest.checkIn : now;
            
            const checkInMonth = checkInDate.getMonth();
            const checkInYear = checkInDate.getFullYear();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
// Check if the booking's check-in month and year match the current month and year
            if (checkInMonth === currentMonth && checkInYear === currentYear) { 
                
                // Revenue platform sees (Base Price + Upsells)
                totalRevenue += (basePrice + upsells);
                totalUpsellRevenue += upsells; // Track total upsells for KPI

                occupiedNights += nights;
// ----------------------------------------------------
                // *** CRITICAL FIX: Base Commission Calculation Logic (FINAL REVISION) ***
                // ----------------------------------------------------
                let baseRateCut = 0;
                
                // isOtaOrUnknown is defined above and incorporates the agreedToTerms check.

                if (isOtaOrUnknown) {
                    // Rule 1: OTA or Unknown Source (Use lower, third-party rate)
                    baseRateCut = thirdPartyRate;
// Increment OTA count for KPI
                    otaCount++;
                } else {
                    // Rule 2: Explicit Direct Booking (agreedToTerms: true) or a non-OTA source
                    baseRateCut = directRate;
// Increment Direct count for KPI
                    directCount++;
                }
                
                // The base commission is calculated using the determined rate
                const baseCommission = basePrice * baseRateCut;
// ----------------------------------------------------
                
                // --- UPDATED COMMISSION CALCULATION (Upsell/Page Split) ---
                // MODIFIED: Use the hotel-specific upsellRate
                const baseUpsellCommission = baseUpsellRevenue * upsellRate;
                const pageCommission = personalizedPageRevenue * PAGE_COMMISSION_RATE;
// 25%
                
                totalCommission += baseCommission + baseUpsellCommission + pageCommission;
            }
        });

        setRevenueData({
            totalRevenue: totalRevenue,
            yourCommission: totalCommission,
            // Display total upsell revenue (10% + 25%)
            upsellRevenue: totalUpsellRevenue, 
            otaCount: otaCount,
            directCount: directCount,
      
 
            avgGuestValue: bookingCount > 0 ? totalGuestValue / bookingCount : 0,
            occupiedNights: occupiedNights,
        });
    }, [guestRecords, hotels]);


    // ============================================================
    // ACTIONS (Unchanged)
    // ============================================================

    // NEW FUNCTION: Handle View Details click 
    const handleViewDetails = (guestId) => {
        // This is the correct navigation call.
        navigate(`/guest-details/${guestId}`);
        console.log(`Attempting navigation to Guest Details page for ID: ${guestId}`);
    };
// ============================================================
    // HOTEL MANAGEMENT HANDLERS (Unchanged)
    // ============================================================
    const handleHotelChange = (e) => {
        const { name, value } = e.target;
// Convert to float for rate fields
        // UPDATED: Added upsellCommissionRate to the float check
        if (name === 'thirdPartyCommissionRate' || name === 'directCommissionRate' || name === 'upsellCommissionRate') {
            setNewHotel((p) => ({ ...p, [name]: parseFloat(value) }));
        } else {
            setNewHotel((p) => ({ ...p, [name]: value }));
        }
    };

    const handleAddHotel = async (e) => {
        e.preventDefault();
// UPDATED: Check for new mandatory fields (Country, State, City)
        if (!newHotel.name.trim() || !newHotel.country.trim() || !newHotel.state.trim() || !newHotel.city.trim()) {
            return setError("Missing required fields: Hotel Name, Country, State, City.");
        }

        try {
            await addDoc(collection(db, 'hotels'), {
                name: newHotel.name.trim(),
                // NEW LOCATION FIELDS
                country: newHotel.country.trim(),
                state: newHotel.state.trim(),
      
                city: newHotel.city.trim(),
                address: newHotel.address.trim(), // Optional

                // COMMISSION RATES
                thirdPartyCommissionRate: parseFloat(newHotel.thirdPartyCommissionRate), 
                directCommissionRate: parseFloat(newHotel.directCommissionRate), // NEW: Save Direct Rate
            
                upsellCommissionRate: parseFloat(newHotel.upsellCommissionRate), // NEW: Save Upsell Rate

                createdAt: new Date().toISOString(),
      
 
                createdBy: auth.currentUser?.email || 'system'
            });
// UPDATED: Reset all fields
            setNewHotel({ 
                name: '', 
                country: '', 
                state: '', 
                city: '', 
           
                address: '',
                thirdPartyCommissionRate: DEFAULT_THIRD_PARTY_RATE, 
                directCommissionRate: DEFAULT_DIRECT_RATE,
                upsellCommissionRate: BASE_UPSELL_COMMISSION_RATE
            });
            await refreshData();
            setSuccess("Hotel added!");
            setError("");
        } catch (err) {
            setError("Failed to add hotel: " + err.message);
            setSuccess("");
        }
    };

    const handleDeleteHotel = async (hotelId) => {
        if (!window.confirm("Delete hotel permanently?")) return;
        try {
            await deleteDoc(doc(db, 'hotels', hotelId));
// Clean up related data
            const roomsQuery = query(collection(db, 'rooms'), where('hotelId', '==', hotelId));
            const roomSnap = await getDocs(roomsQuery);
            roomSnap.docs.forEach(async (d) => deleteDoc(doc(db, 'rooms', d.id)));

            const guestsQuery = query(collection(db, 'guests'), where('hotelId', '==', hotelId));
            const guestSnap = await getDocs(guestsQuery);
            guestSnap.docs.forEach(async (d) => deleteDoc(doc(db, 'guests', d.id)));
            
            await refreshData();
            setSuccess("Hotel, rooms, and guests deleted.");
            setError("");
        } catch (err) {
            setError("Delete failed: " + err.message);
            setSuccess("");
        }
    };

    // ============================================================
    // ROOM MANAGEMENT HANDLERS (Unchanged)
    // ============================================================
    const handleRoomChange = (e) => {
        const { name, value } = e.target;
        setNewRoom((p) => ({ ...p, [name]: value }));
    };

    const handleAddRoom = async (e) => {
        e.preventDefault();
        if (!newRoom.hotelId || !newRoom.roomNumber || !newRoom.price)
            return setError("Missing required fields for room.");
        try {
            await addDoc(collection(db, 'rooms'), {
                ...newRoom,
                price: parseFloat(newRoom.price),
                createdAt: new Date().toISOString()
            });
            setNewRoom(initialRoomState);
            await refreshData();
            setSuccess("Room added!");
            setError("");
        } catch (err) {
            setError("Failed to add room: " + err.message);
            setSuccess("");
        }
    };

    // ============================================================
    // USER MANAGEMENT HANDLERS (Staff Creation)
    // ============================================================
    const handleUserChange = (e) => {
        const { name, value } = e.target;
        let update = { [name]: value };
        if (name === 'hotelId') {
            update.hotelName = hotels.find((h) => h.id === value)?.name ||
            '';
        }

        setNewUser((p) => ({ ...p, ...update }));
    };
    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!newUser.email || !newUser.password)
            return setError("Fill all fields for new user.");
        try {
            const account = await createUserWithEmailAndPassword(auth, newUser.email, newUser.password);
            const uid = account.user.uid;

            await setDoc(doc(db, 'users', uid), {
                email: newUser.email.toLowerCase(),
                role: newUser.role,
                hotelId: newUser.role === 'hotel' ? newUser.hotelId : null,
                hotelName: newUser.role === 'hotel' ? newUser.hotelName : null,
            
 
 
                createdAt: new Date().toISOString(),
                createdBy: auth.currentUser?.email || 'system',
                // Initialize dashboard codes so the guard doesn't fail on first staff login
                dashboardCodes: {
                
 
                    reception: "",
                    housekeeping: "",
                    inventory: "",
                    manager: ""
              
 }
            });
            setNewUser(initialUserState);
            await refreshData();
// Refresh list after adding
            setSuccess(`User ${newUser.email} created!`);
            setError("");
        } catch (err) {
            setError("User creation failed: " + err.message);
            setSuccess("");
        }
    };
    
    // --- NEW DELETE USER HANDLER (ADDED) ---
    const handleDeleteUser = async (uid) => {
        if (!window.confirm("Delete this user permanently? This will only remove their Firestore record, not their Firebase Auth account.")) return;
        try {
            // Note: Deleting the Firebase Auth user requires Admin SDK/Cloud Function, 
            // but deleting the Firestore record is enough to block dashboard access.
            await deleteDoc(doc(db, 'users', uid));
            await refreshData();
            setSuccess("User record deleted!");
            setError("");
        } catch (err) {
            setError("Failed to delete user: " + err.message);
            setSuccess("");
        }
    };
    // --- END NEW DELETE USER HANDLER ---
    
    // ============================================================
    // HOTEL CONTACTS MANAGEMENT HANDLERS (Unchanged)
    // ============================================================
    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setNewContact((p) => ({ ...p, [name]: value }));
    };

    const handleAddContact = async (e) => {
        e.preventDefault();
        if (!newContact.hotelName.trim() || !newContact.city.trim() || !newContact.country.trim()) {
            return setError("Hotel Name, City, and Country are required fields.");
        }

        try {
            await addDoc(collection(db, 'hotel_contacts'), {
                ...newContact,
                createdAt: new Date().toISOString(),
                createdBy: auth.currentUser?.email || 'system'
            });
            setNewContact({ hotelName: '', city: '', country: '', contactInfo: '', notes: '' });
            await refreshData();
            setSuccess("Hotel contact added!");
            setError("");
        } catch (err) {
            setError("Failed to add contact: " + err.message);
            setSuccess("");
        }
    };

    const handleDeleteContact = async (contactId) => {
        if (!window.confirm("Delete hotel contact permanently?")) return;
        try {
            await deleteDoc(doc(db, 'hotel_contacts', contactId));
            await refreshData();
            setSuccess("Hotel contact deleted.");
            setError("");
        } catch (err) {
            setError("Delete failed: " + err.message);
            setSuccess("");
        }
    };


    if (loading) return <div className="container"><div className="card">Loading...</div></div>;
    if (user && user.role !== 'admin') return <div className="container"><div className="error-message info-box">Access Denied</div></div>;
// ============================================================
    // RENDER SECTION FUNCTIONS
    // ============================================================

    // --- Hotel Management Section (UPDATED) ---
    const renderHotelSection = () => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
            {/* Column 1: Add Hotel Form */}
            <div className="card">
                <h3>Add New 
                Hotel</h3>
                <form onSubmit={handleAddHotel}>
                    <label>Hotel Name *</label>
                    <input name="name" value={newHotel.name} onChange={handleHotelChange} required />
                    
                
                    {/* NEW LOCATION FIELDS */}
                    <label>Country *</label>
                    <input name="country" value={newHotel.country} onChange={handleHotelChange} required />

                    <label>State *</label>
                    <input name="state" value={newHotel.state} onChange={handleHotelChange} required 
/>
                    
                    <label>City *</label>
                    <input name="city" value={newHotel.city} onChange={handleHotelChange} required />

                    <label>Address (Optional)</label>
             
                    <input name="address" value={newHotel.address} onChange={handleHotelChange} />
                    
                    {/* COMMISSION FIELDS */}
                    <label>OTA Commission Rate (e.g., 0.02 for 2%)</label>
                    
                    <input
                        type="number"
                        step="0.001"
                        name="thirdPartyCommissionRate"
                        value={newHotel.thirdPartyCommissionRate}
    
                        onChange={handleHotelChange}
                        required
                    />
                    <p style={{ fontSize: '0.8em', color: '#666', marginTop: '-10px', marginBottom: '15px' }}>
      
                        *This rate is applied to the base price of OTA/Third Party bookings.
</p>

                    <label>Direct/Staff Commission Rate (e.g., 0.08 for 8%)</label>
                    <input
                        type="number"
                        step="0.001"
      
                        name="directCommissionRate"
                        value={newHotel.directCommissionRate}
                        onChange={handleHotelChange}
                        required
          
           />
                    <p style={{ fontSize: '0.8em', color: '#666', marginTop: '-10px', marginBottom: '15px' }}>
                        *This rate is applied to the base price of Direct and Staff bookings.
</p>
                    
                    {/* NEW UPSELL COMMISSION FIELD */}
                    <label>Upsells Commission Rate (e.g., 0.10 for 10%) *</label>
                    <input
        
                        type="number"
                        step="0.001"
                        name="upsellCommissionRate"
                        value={newHotel.upsellCommissionRate}
            
                        onChange={handleHotelChange}
                        required
                    />
                    <p style={{ fontSize: '0.8em', color: '#666', marginTop: '-10px', marginBottom: '15px' }}>
              
                        *This rate is applied to general upsell revenue (not personalized page revenue).
</p>

                    <button type="submit">Add Hotel</button>
                </form>
            </div>
            {/* Column 2: Hotel List and Deletion */}
            <div className="card">
                <h3>Existing Hotels 
                ({hotels.length})</h3>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                    {hotels.map((hotel) => (
                        <li key={hotel.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <span style={{ fontWeight: 'bold' }}>{hotel.name}</span>
                                <button
                                    onClick={() => handleDeleteHotel(hotel.id)}
                                    style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer' }}
                                >
                                    Delete
                                </button>
                            </div>
                            <small style={{ color: '#555', marginTop: '5px' }}>
                                Location: {hotel.city}, {hotel.state}, {hotel.country}
                            </small>
                            <small style={{ color: '#007bff', marginTop: '5px', display: 'flex', flexDirection: 'column' }}>
                                <span>OTA Cut: {((hotel.thirdPartyCommissionRate || DEFAULT_THIRD_PARTY_RATE) * 100).toFixed(1)}%</span>
                                <span>Direct Cut: {((hotel.directCommissionRate || DEFAULT_DIRECT_RATE) * 100).toFixed(1)}%</span>
                                <span>Upsell Cut: {((hotel.upsellCommissionRate || BASE_UPSELL_COMMISSION_RATE) * 100).toFixed(1)}%</span>
                            </small>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
    // --- Room Management Section (Unchanged) ---
    const renderRoomSection = () => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
            {/* Column 1: Add Room Form */}
            <div className="card">
                <h3>Add New Room</h3>
                <form onSubmit={handleAddRoom}>
                    <label>Assign Hotel</label>
                    <select name="hotelId" value={newRoom.hotelId} onChange={handleRoomChange} required>
                        <option value="">Select Hotel</option>
                        {hotels.map((h) => (
                            <option key={h.id} value={h.id}>{h.name}</option>
                        ))}
                    </select>
                    <label>Room Number</label>
                    <input name="roomNumber" value={newRoom.roomNumber} onChange={handleRoomChange} required />
                    <label>Price</label>
                    <input type="number" name="price" value={newRoom.price} onChange={handleRoomChange} required />
                    <label>Type</label>
                    <select name="type" value={newRoom.type} onChange={handleRoomChange}>
                        <option value="Standard">Standard</option>
                        <option value="Deluxe">Deluxe</option>
                        <option value="Suite">Suite</option>
                    </select>
                    <button type="submit">Add Room</button>
                </form>
            </div>
            {/* Column 2: Room List */}
            <div className="card">
                <h3>Existing Rooms 
                ({rooms.length})</h3>
                <label>Filter by Hotel</label>
                <select onChange={(e) => setSelectedHotelId(e.target.value)} value={selectedHotelId}>
                    <option value="">All Hotels</option>
                    {hotels.map((h) => (
                        <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                </select>
                <ul style={{ listStyle: 'none', padding: 0, maxHeight: '400px', overflowY: 'auto' }}>
                    {rooms.filter(r => !selectedHotelId ||
                    r.hotelId === selectedHotelId).map((room) => (
                        <li key={room.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <span style={{ fontWeight: 'bold' }}>{room.roomNumber} - {room.type}</span>
                            <small style={{ color: '#555' }}>{getHotelName(room.hotelId)}</small>
                            <span style={{ color: '#007bff' }}>${parseFloat(room.price).toFixed(2)}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
    // --- Staff Management Section (Unchanged) ---
    const renderStaffSection = () => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
            {/* Column 1: Add User Form */}
            <div className="card">
                <h3>Add New Staff/Manager User</h3>
                <form onSubmit={handleAddUser}>
                    <label>Email</label>
                    <input name="email" type="email" value={newUser.email} onChange={handleUserChange} required />
                    <label>Password</label>
                    <input name="password" type="password" value={newUser.password} onChange={handleUserChange} required />
                    <label>Role</label>
                    <select name="role" value={newUser.role} onChange={handleUserChange}>
                        <option value="hotel">Staff (Basic)</option>
                        <option value="manager">Manager</option>
                    </select>
                    <label>Assign Hotel (if not Admin)</label>
                    <select name="hotelId" value={newUser.hotelId} onChange={handleUserChange} required={newUser.role !== 'admin'}>
                        <option value="">Select Hotel</option>
                        {hotels.map((h) => (
                            <option key={h.id} value={h.id}>{h.name}</option>
                        ))}
                    </select>
                    <button type="submit">Create User</button>
                </form>
            </div>
            {/* Column 2: Staff List */}
            <div className="card">
                <h3>Existing Staff Users ({staff.length})</h3>
                <ul style={{ listStyle: 'none', padding: 0, maxHeight: '400px', overflowY: 'auto' }}>
                    {staff.map((staffMember) => (
                        <li key={staffMember.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <span style={{ fontWeight: 'bold' }}>{staffMember.email}</span>
                            <small style={{ color: staffMember.role === 'manager'
                            ? '#dc3545' : '#007bff', fontSize: '0.8em' }}> Role: {staffMember.role} {staffMember.hotelName ?
                            ` (${staffMember.hotelName})` : ''} </small>
                            <button
                                // *** FIX: Used 'staffMember.id' instead of the undefined 'userId' ***
                                onClick={() => handleDeleteUser(staffMember.id)}
                                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer', marginLeft: '10px' }}
                            >
                                Delete
                            </button>
                        </li>
                    ))}
                    {staff.length === 0 && <li>No staff accounts found.</li>}
                </ul>
            </div>
        </div>
    );
    // --- Access Codes Section (Unchanged) ---
    const renderCodesSection = () => {
        // Functionality for loading and saving codes (Unchanged)
        const loadCodes = async (hotelId) => {
            if (!hotelId) return setDashboardCodes({ reception: '', housekeeping: '', inventory: '', manager: '' });
            try {
                // Find any staff user for this hotel
                const usersQuery = query(
                    collection(db, 'users'),
                    where('hotelId', '==', hotelId),
                    where('role', 'in', ['hotel', 'manager'])
                );
                const userSnap = await getDocs(usersQuery);
                if (userSnap.empty) {
                    // Reset if no staff is found
                    setDashboardCodes({ reception: '', housekeeping: '', inventory: '', manager: '' });
                    setError(`No staff found for hotel ID: ${hotelId}.`);
                    setSuccess("");
                    return;
                }
                // Use the codes from the first staff member found
                const codes = userSnap.docs[0].data().dashboardCodes ||
                { reception: '', housekeeping: '', inventory: '', manager: '' };
                setDashboardCodes(codes);
                setError("");
            } catch (err) {
                console.error("Failed to load codes:", err);
                setError("Failed to load dashboard codes. Check console.");
                setSuccess("");
            }
        };
        const saveCodes = async () => {
            try {
                if (!selectedHotelId) return;
                const usersQuery = query(
                    collection(db, 'users'),
                    where('hotelId', '==', selectedHotelId),
                    where('role', 'in', ['hotel', 'manager'])
                );
                const userSnap = await getDocs(usersQuery);
                if (userSnap.empty) {
                    setError(`No staff found for hotel ID: ${selectedHotelId}. Codes not saved.`);
                    setSuccess("");
                    return;
                }
                const updates = userSnap.docs.map(userDoc => {
                    const userDocRef = doc(db, 'users', userDoc.id);
                    return setDoc(userDocRef, { dashboardCodes: dashboardCodes }, { merge: true });
                });
                await Promise.all(updates);
                setSuccess(`Dashboard codes updated successfully for ${userSnap.size} staff members!`);
                setError("");
            } catch (err) {
                console.error("Failed to save codes:", err);
                setError("Failed to save codes.");
                setSuccess("");
            }
        };

        return (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
                {/* Column 1: Dashboard Codes Manager */}
                <div className="card">
                    <h3>Manage Dashboard Access Codes</h3>
                    <label>Select Hotel</label>
                    <select
                        onChange={(e) => {
                            setSelectedHotelId(e.target.value);
                            loadCodes(e.target.value);
                        }}
                        value={selectedHotelId}
                        required
                    >
                        <option value="">-- Select Hotel --</option>
                        {hotels.map((h) => (
                            <option key={h.id} value={h.id}>{h.name}</option>
                        ))}
                    </select>
                    {selectedHotelId && (
                        <>
                            <label>Reception Code</label>
                            <input name="reception" value={dashboardCodes.reception} onChange={(e) => setDashboardCodes(p => ({ ...p, reception: e.target.value }))} />
                            <label>Housekeeping Code</label>
                            <input name="housekeeping" value={dashboardCodes.housekeeping} onChange={(e) => setDashboardCodes(p => ({ ...p, housekeeping: e.target.value }))} />
                            <label>Inventory Code</label>
                            <input name="inventory" value={dashboardCodes.inventory} onChange={(e) => setDashboardCodes(p => ({ ...p, inventory: e.target.value }))} />
                            <label>Manager Code</label>
                            <input name="manager" value={dashboardCodes.manager} onChange={(e) => setDashboardCodes(p => ({ ...p, manager: e.target.value }))} />
                            <button onClick={saveCodes} style={{ marginTop: '15px' }}>
                                Save Codes
                            </button>
                        </>
                    )}
                </div>
                {/* Column 2: Explanation/Instructions */}
                <div className="card" style={{ backgroundColor: '#f9f9f9', borderLeft: '5px solid #ffc107' }}>
                    <h3>How Access Codes Work</h3>
                    <p>Access codes are used by the staff login page to determine which dashboard (Reception, Housekeeping, Inventory, or Manager) a user can access without a full staff account.</p>
                    <p>Codes are saved directly to all staff user accounts assigned to the selected hotel.</p>
                    <p><strong>Note:</strong> A single staff account can only access one dashboard role (hotel role defaults to reception access if no codes are set).</p>
                    <p>Leaving a code field blank 
                    disables access for that specific dashboard.</p>
                </div>
            </div>
        );
    };
    // --- Revenue Section (Unchanged, but calculations use new commission rate) ---
    const renderRevenueSection = () => {
        const data = revenueData;
        const formatRate = (rate) => `${(rate * 100).toFixed(1)}%`;
        return (
            <div style={{ marginTop: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
                    {/* Primary KPIs */}
                    <div className="card" style={{ padding: '15px', textAlign: 'center', backgroundColor: '#f9f9f9', borderLeft: '5px solid #007bff' }}>
                        <h4 style={{ margin: 0, color: '#007bff' }}>Total Revenue This Month</h4>
                        <p style={{ fontSize: '1.8em', margin: '5px 0' }}>${data.totalRevenue.toFixed(2)}</p>
                    </div>
                    <div className="card" style={{ padding: '15px', textAlign: 'center', backgroundColor: '#f9f9f9', borderLeft: '5px solid #28a745' }}>
                        <h4 style={{ margin: 0, color: '#28a745' }}>Your Commission This Month</h4>
                        <p style={{ fontSize: '1.8em', margin: '5px 0' }}>${data.yourCommission.toFixed(2)}</p>
                    </div>
                    <div className="card" style={{ padding: '15px', textAlign: 'center', backgroundColor: '#f9f9f9', borderLeft: '5px solid #ffc107' }}>
                        <h4 style={{ margin: 0, color: '#ffc107' }}>Avg Guest Value (All Time)</h4>
                        <p style={{ fontSize: '1.8em', margin: '5px 0' }}>${data.avgGuestValue.toFixed(2)}</p>
                    </div>
                    <div className="card" style={{ padding: '15px', textAlign: 'center', backgroundColor: '#f9f9f9', borderLeft: '5px solid #17a2b8' }}>
                        <h4 style={{ margin: 0, color: '#17a2b8' }}>Occupied Nights (This Month)</h4>
                        <p style={{ fontSize: '1.8em', margin: '5px 0' }}>{data.occupiedNights}</p>
                    </div>
                    {/* Secondary KPIs */}
                    <div style={{ gridColumn: 'span 4', display: 'flex', justifyContent: 'space-around', padding: '10px', backgroundColor: '#eee', borderRadius: '5px' }}>
                        <span>**Upsell Revenue (This Month): ${data.upsellRevenue.toFixed(2)}**</span>
                        <span>**OTA Bookings (This Month): {data.otaCount}**</span>
                        <span>**Direct/Staff Bookings (This Month): {data.directCount}**</span>
                    </div>
                </div>
                {/* Transaction List */}
                <div className="card">
                    <h3>Recent Guest Records (Last 50 Records)</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9em' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <th style={{ padding: '10px 5px' }}>Hotel</th>
                                <th style={{ padding: '10px 5px' }}>Type (Base)</th>
                                <th style={{ padding: '10px 5px' }}>Base Price</th>
                                <th style={{ padding: '10px 5px' }}>Total Upsells</th>
                                <th style={{ padding: '10px 5px' }}>Rates Applied</th>
                                <th style={{ padding: '10px 5px' }}>Check-In</th>
                                <th style={{ padding: '10px 5px' }}>Your Commission</th>
                                <th style={{ padding: '10px 5px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {guestRecords.slice(0, 50).map((guest) => { // Limit to 50 for performance
                                // 🚨 Data calculation for table row (MUST MATCH useEffect logic)
                                const rawUpsells = parseFloat(guest.addOnsPrice) ||
                                0;
                                const totalFinalPrice = parseFloat(guest.adjustedFinalPrice) || 0;
                                // Check for Direct Booking
                                const taxCollected = parseFloat(guest.taxCollected) || 0;
                                const depositCollected = parseFloat(guest.depositCollected) || 0;
                                const basePriceForCommission = totalFinalPrice - rawUpsells - taxCollected - depositCollected;
                                const basePrice = Math.max(0, basePriceForCommission);
                                const upsells = Math.max(0, rawUpsells);
                                
                                // CORE // FIX: Normalize the source for reliable comparison
                                const rawSource = guest.bookingSource || '';
                                const source = rawSource.trim().toUpperCase();
                                
                                // *** NEW LOGIC PER USER REQUEST (Must match useEffect) ***
                                const isDirectBookingByTerms = guest.agreedToTerms === true;
                                const isSourceOta = OTA_IDENTIFIERS.some(identifier => source.includes(identifier.replace('.', '')));
                                const isSourcePresent = source.length > 0;
                                const isOtaOrUnknown = !isDirectBookingByTerms && (isSourceOta || !isSourcePresent);
                                const isOtaBooking = isSourceOta;
                                // *** END NEW LOGIC ***
                                
                                // Get the specific hotel's commission rate
                                const hotel = hotels.find(h => h.id === guest.hotelId);
                                let thirdPartyRate = parseFloat(hotel?.thirdPartyCommissionRate) || DEFAULT_THIRD_PARTY_RATE;
                                let directRate = parseFloat(hotel?.directCommissionRate) || DEFAULT_DIRECT_RATE;
                                let upsellRate = parseFloat(hotel?.upsellCommissionRate) || BASE_UPSELL_COMMISSION_RATE;
                                
                                // Commission logic
                                let baseRateCut = isOtaOrUnknown ? thirdPartyRate : directRate;
                                
                                const personalizedPageRevenue = parseFloat(guest.personalizedPageRevenue) || 0;
                                const baseUpsellRevenue = Math.max(0, upsells - personalizedPageRevenue);

                                const baseCommission = basePrice * baseRateCut;
                                const baseUpsellCommission = baseUpsellRevenue * upsellRate;
                                const pageCommission = personalizedPageRevenue * PAGE_COMMISSION_RATE;
                                const totalRowCommission = baseCommission + baseUpsellCommission + pageCommission;

                                // Type for display
                                let determinedType = 'Unknown';
                                if(isDirectBookingByTerms) {
                                    determinedType = 'Direct (Agreed Terms)';
                                } else if(isOtaBooking) {
                                    determinedType = 'OTA/3rd Party';
                                } else if(isSourcePresent) {
                                    determinedType = 'Direct (Source)';
                                } else {
                                    determinedType = 'Unknown';
                                }

                                // --- DYNAMIC DISPLAY FIX STARTS HERE ---
                                let fullRateDisplay = `${(baseRateCut * 100).toFixed(1)}% (Base)`;
                                const baseUpsellCommissions = upsellRate * 100; // Use hotel specific rate
                                const pageCommissions = PAGE_COMMISSION_RATE * 100;

                                // Only show upsell rates if there was upsell revenue
                                if (upsells > 0) {
                                    fullRateDisplay += ` + ${baseUpsellCommissions.toFixed(0)}% (Upsell)`;
                                    if (personalizedPageRevenue > 0) {
                                        fullRateDisplay += ` + ${pageCommissions.toFixed(0)}% (Page)`;
                                    }
                                }
                                // --- DYNAMIC DISPLAY FIX ENDS HERE ---
                                // Date formatting
                                const checkInDisplay = guest.checkIn instanceof Date && !isNaN(guest.checkIn) ?
                                guest.checkIn.toLocaleDateString() : 'N/A';

                                return (
                                    <tr key={guest.id} style={{ borderBottom: '1px solid #eee' }}>
                                        <td style={{ padding: '10px 5px' }}>{getHotelName(guest.hotelId)}</td>
                                        <td style={{ padding: '10px 5px' }}>
                                            {determinedType}
                                            <br />
                                            <small style={{ color: determinedType.includes('OTA') || determinedType.includes('Unknown') ? '#dc3545' : '#28a745' }}>
                                                Source: {source || 'N/A'}
                                            </small>
                                        </td>
                                        <td style={{ padding: '10px 5px' }}>${basePrice.toFixed(2)}</td>
                                        <td style={{ padding: '10px 5px' }}>${upsells.toFixed(2)}</td>
                                        <td style={{ padding: '10px 5px', fontSize: '0.85em' }}>{fullRateDisplay}</td>
                                        <td style={{ padding: '10px 5px' }}>{checkInDisplay}</td>
                                        <td style={{ padding: '10px 5px', fontWeight: 'bold' }}>${totalRowCommission.toFixed(2)}</td>
                                        <td style={{ padding: '10px 5px' }}>
                                            <button
                                                onClick={() => handleViewDetails(guest.id)}
                                                style={{ padding: '5px 10px', border: '1px solid #007bff', borderRadius: '3px', backgroundColor: 'white', color: '#007bff', cursor: 
                                                'pointer' }}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {guestRecords.length === 0 && <p style={{ textAlign: 'center' }}>No guest records found.</p>}
                </div>
            </div>
        );
    };

    // --- Hotel Contacts Section (Unchanged) ---
    const renderContactsSection = () => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px' }}>
            {/* Column 1: Add Contact Form */}
            <div className="card">
                <h3>Add New Hotel Contact</h3>
                <form onSubmit={handleAddContact}>
                    <label>Hotel Name *</label>
                    <input name="hotelName" value={newContact.hotelName} onChange={handleContactChange} required />
                    
                    <label>City *</label>
                    <input name="city" value={newContact.city} onChange={handleContactChange} required />

                    <label>Country *</label>
                    <input name="country" value={newContact.country} onChange={handleContactChange} required />
                    
                    <label>Contact Info (Email, Phone, etc.)</label>
                    <input name="contactInfo" value={newContact.contactInfo} onChange={handleContactChange} />

                    <label>Notes (e.g., Key Decision Maker)</label>
                    <textarea name="notes" value={newContact.notes} onChange={handleContactChange} rows="3" />
                    
                    <button type="submit">Add Contact</button>
                </form>
            </div>
            {/* Column 2: Contact List */}
            <div className="card">
                <h3>Existing Hotel Contacts ({hotelContacts.length})</h3>
                <ul style={{ listStyle: 'none', padding: 0, maxHeight: '400px', overflowY: 'auto' }}>
                    {hotelContacts.map((contact) => (
                        <li key={contact.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 
                            'center' }}>
                                <span style={{ fontWeight: 'bold' }}>{contact.hotelName}</span>
                                <button
                                    onClick={() => handleDeleteContact(contact.id)}
                                    style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer' }}
                                >
                                    Delete
                                </button>
                            </div>
                            <small style={{ color: '#555', marginTop: '5px' }}> Location: {contact.city}, {contact.country} </small>
                            {contact.contactInfo && (
                                <small style={{ color: '#007bff', marginTop: '5px' }}> Contact: {contact.contactInfo} </small>
                            )}
                            {contact.notes && (
                                <small style={{ color: '#28a745', fontStyle: 'italic', marginTop: '5px', wordBreak: 'break-word' }}> Notes: {contact.notes.substring(0, 100)}... </small>
                            )}
                        </li>
                    ))}
                    {hotelContacts.length === 0 && <li>No hotel contacts found.</li>}
                </ul>
            </div>
        </div>
    );


    return (
        <div className="container">
            <div className="card admin-dashboard">
                <h1>Admin Dashboard</h1>
                {error && <p className="error-message">{error}</p>}
                {success && <p className="success-message">{success}</p>}

                {/* Tab Navigation (UPDATED) */}
                <div style={{ borderBottom: '2px solid #ddd', marginBottom: '20px', display: 'flex' }}>
                    <TabButton name="Hotels" active={activeSection === 'hotels'} onClick={setActiveSection} />
                    <TabButton name="Rooms" active={activeSection === 'rooms'} onClick={setActiveSection} />
                    <TabButton name="Staff" active={activeSection === 'staff'} onClick={setActiveSection} />
                    <TabButton name="Access Codes" active={activeSection === 'accesscodes'} onClick={setActiveSection} />
                    <TabButton name="Revenue" active={activeSection === 'revenue'} onClick={setActiveSection} />
                    {/* NEW TAB: Contacts */}
                    <TabButton name="Contacts" active={activeSection === 'contacts'} onClick={setActiveSection} />
                </div>

                {/* Section Content (UPDATED) */}
                {activeSection === 'hotels' && renderHotelSection()}
                {activeSection === 'rooms' && renderRoomSection()}
                {activeSection === 'staff' && renderStaffSection()}
                {activeSection === 'accesscodes' && renderCodesSection()}
                {activeSection === 'revenue' && renderRevenueSection()}
                {/* NEW SECTION: Contacts */}
                {activeSection === 'contacts' && renderContactsSection()}

            </div>
        </div>
    );
}